import { UserRole, SubscriptionPlanTier, Organization } from '../types';
import { getPlanLevel } from './subscriptionPlans';

export interface FeatureConfig {
  name: string;
  requiredPlan?: SubscriptionPlanTier;
  requiredRole?: UserRole;
  enabled: boolean;
}

// Feature availability matrix
const FEATURES: Record<string, FeatureConfig> = {
  // Basic features available to all plans
  clients: {
    name: 'Gestión de Clientes',
    enabled: true,
  },
  cases: {
    name: 'Gestión de Casos',
    enabled: true,
  },
  accounting: {
    name: 'Planificación de pagos',
    enabled: true,
  },
  expenses: {
    name: 'Gastos',
    enabled: true,
  },

  // Pro features
  advancedReports: {
    name: 'Reportes Avanzados',
    requiredPlan: 'pro',
    enabled: true,
  },
  integrations: {
    name: 'Integraciones',
    requiredPlan: 'pro',
    enabled: true,
  },

  // Enterprise features
  apiAccess: {
    name: 'Acceso a API',
    requiredPlan: 'enterprise',
    enabled: true,
  },
  customIntegrations: {
    name: 'Integraciones Personalizadas',
    requiredPlan: 'enterprise',
    enabled: true,
  },

  // Admin features
  userManagement: {
    name: 'Gestión de Usuarios',
    requiredRole: 'admin',
    enabled: true,
  },
  subscriptionManagement: {
    name: 'Gestión de Suscripción',
    requiredRole: 'admin',
    enabled: true,
  },
  // Menú Proveedores (oculto por defecto; poner enabled: true para mostrarlo)
  providers: {
    name: 'Proveedores',
    enabled: false,
  },

  // Superadmin features
  platformManagement: {
    name: 'Gestión de Plataforma',
    requiredRole: 'superadmin',
    enabled: true,
  },
};

export const featureFlags = {
  isFeatureEnabled: (
    featureName: string,
    userRole: UserRole,
    organization?: Organization
  ): boolean => {
    const feature = FEATURES[featureName];
    if (!feature || !feature.enabled) {
      return false;
    }

    // Check role requirement
    if (feature.requiredRole) {
      const roleHierarchy: Record<UserRole, number> = {
        regular: 1,
        admin: 2,
        superadmin: 3,
      };
      const userLevel = roleHierarchy[userRole] || 0;
      const requiredLevel = roleHierarchy[feature.requiredRole] || 0;
      if (userLevel < requiredLevel) {
        return false;
      }
    }

    // Check plan requirement
    if (feature.requiredPlan && organization) {
      const userPlan = organization.subscriptionPlan as SubscriptionPlanTier;
      const userPlanLevel = getPlanLevel(userPlan);
      const requiredPlanLevel = getPlanLevel(feature.requiredPlan);
      if (userPlanLevel < requiredPlanLevel) {
        return false;
      }
    }

    return true;
  },

  getAvailableFeatures: (userRole: UserRole, organization?: Organization): string[] => {
    return Object.keys(FEATURES).filter((featureName) =>
      featureFlags.isFeatureEnabled(featureName, userRole, organization)
    );
  },

  getFeatureConfig: (featureName: string): FeatureConfig | null => {
    return FEATURES[featureName] || null;
  },
};

